import { useSyncExternalStore } from 'react';
import Swal from 'sweetalert2';

export interface AccountIdentity {
  account_id: string;
  name: string;
  picture_url: string | null;
}

const ACCOUNT_STORAGE_KEY = 'portal.accountId';
const PLAN_DRAFT_PREFIX = 'brainy-plan-studio:';
const NAVIGATION_PREFIX = 'brainy-navigation:';

let generation = 0;
const createSession = (account: AccountIdentity | null) => ({
  account,
  generation: generation++,
  controller: new AbortController(),
});

let session = createSession(null);
const listeners = new Set<() => void>();

export const getAccountSession = () => session;

export const subscribeAccountSession = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useAccountSession = () =>
  useSyncExternalStore(subscribeAccountSession, getAccountSession);

const replaceSession = (account: AccountIdentity | null) => {
  const previous = session;
  session = createSession(account);
  previous.controller.abort();
  // These dialogs live outside React, so unmounting the account UI leaves them open.
  Swal.close();
  listeners.forEach((listener) => listener());
  return session;
};

const removeStoredAccountData = (
  keepDraftPrefix?: string,
  keepNavigationPrefix?: string
) => {
  for (const storageName of ['localStorage', 'sessionStorage'] as const) {
    try {
      const storage = window[storageName];
      const keys = Array.from({ length: storage.length }, (_, index) =>
        storage.key(index)
      );
      for (const key of keys) {
        if (
          key &&
          (key === 'app-store' ||
            key === 'activeProfileId' ||
            key === 'portal.account.avatar.retry_until.v1' ||
            (key.startsWith(PLAN_DRAFT_PREFIX) &&
              (!keepDraftPrefix || !key.startsWith(keepDraftPrefix))) ||
            (key.startsWith(NAVIGATION_PREFIX) &&
              (!keepNavigationPrefix || !key.startsWith(keepNavigationPrefix))))
        ) {
          storage.removeItem(key);
        }
      }
    } catch {
      // Account data stays in memory when browser storage is unavailable.
    }
  }
};

const readStoredAccountId = (): string | null => {
  try {
    return window.localStorage.getItem(ACCOUNT_STORAGE_KEY);
  } catch {
    return null;
  }
};

// No account data is available while identity is being verified. Keep stored state
// inaccessible until the server confirms their owner, including after reload.
export const beginAccountCheck = () => {
  removeStoredAccountData(PLAN_DRAFT_PREFIX, NAVIGATION_PREFIX);
  return replaceSession(null);
};

export const startAccountSession = (account: AccountIdentity) => {
  const sameAccount = readStoredAccountId() === account.account_id;
  removeStoredAccountData(
    sameAccount
      ? `${PLAN_DRAFT_PREFIX}${encodeURIComponent(account.account_id)}:`
      : undefined,
    sameAccount
      ? `${NAVIGATION_PREFIX}${encodeURIComponent(account.account_id)}:`
      : undefined
  );
  try {
    window.localStorage.setItem(ACCOUNT_STORAGE_KEY, account.account_id);
  } catch {
    // Draft persistence is disabled if its ownership cannot be stored.
  }
  replaceSession(account);
};

export const endAccountSession = () => {
  replaceSession(null);
  removeStoredAccountData();
  try {
    window.localStorage.removeItem(ACCOUNT_STORAGE_KEY);
  } catch {
    // Logout must still proceed when storage is unavailable.
  }
};

export const getAccountDraftPrefix = (): string | null => {
  const accountId = session.account?.account_id;
  return accountId && readStoredAccountId() === accountId
    ? `${PLAN_DRAFT_PREFIX}${encodeURIComponent(accountId)}:`
    : null;
};

export const getAccountNavigationKey = (): string | null => {
  const accountId = session.account?.account_id;
  return accountId && readStoredAccountId() === accountId
    ? `${NAVIGATION_PREFIX}${encodeURIComponent(accountId)}:context`
    : null;
};

export const canAccessAccountDraft = (storageKey: string): boolean => {
  const prefix = getAccountDraftPrefix();
  return prefix !== null && storageKey.startsWith(prefix);
};

export const isAccountStorageEvent = (event: StorageEvent): boolean =>
  (event.key === ACCOUNT_STORAGE_KEY || event.key === null) &&
  event.newValue !== session.account?.account_id &&
  event.newValue === readStoredAccountId();
